/**
 * exportSummaryExcel.js
 * Complete CBAM Summary Excel export — all 3 sheets fully implemented.
 *
 * Sheet 1 — Summary_Processes  (mirrors Summary1Installation.js)
 *   Section 1: Installation details + production processes + purchased precursors
 *   Section 2a: GHG balance table (per process + totals + comparison)
 *   Section 2b: SEE table (processes + precursors)
 *   Section 3:  Detailed overview per process (with precursor sub-rows)
 *
 * Sheet 2 — Summary_Products  (mirrors SummaryProducts.js — all 44 columns)
 *   Production process rows (from FormD via D.calculateSEE)
 *   Purchased precursor rows (from FormE)
 *
 * Sheet 3 — Summary_Communication  (mirrors SummaryCommunication.js)
 *   Section 1: Installation details + routes + emissions by methodology
 *   Section 2: 44-column product communication table
 */

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { D as DFormulas } from "./CbamFormulas";
import { getDescriptionForCompactCode, getDisplayCodeForCompact } from "./cnCodeLookup";
// ─── Colour palette ───────────────────────────────────────────────────────────
const C = {
  DARK_TEAL:    "FF376366", // Section band headers (dark teal)
  MID_TEAL:     "FF4D8285", // Sub-headers
  PALE_TEAL:    "FFE5F0F1", // ID cells
  GREEN:        "FFCCFFCC", // Auto-filled value cells
  YELLOW:       "FFFFFF00", // Required manual input
  LT_YELLOW: "FFFFFFCC",  // Optional / conditional
  TOTAL_BLUE:   "FFD9E1F2", // Total/sum rows
  LABEL_GREY:   "FFF2F2F2", // Label cells
  NOTE_YELLOW:  "FFFFF2CC", // Note cells
  WHITE:        "FFFFFFFF",
  BORDER:       "FF7A7A7A",
};

// ─── Style helpers ────────────────────────────────────────────────────────────
const border = () => ({
  top:    { style: "thin", color: { argb: C.BORDER } },
  left:   { style: "thin", color: { argb: C.BORDER } },
  bottom: { style: "thin", color: { argb: C.BORDER } },
  right:  { style: "thin", color: { argb: C.BORDER } },
});

const fill = (argb) => ({
  type: "pattern", pattern: "solid", fgColor: { argb },
});

/**
 * Style a cell with fill + border + alignment.
 * @param {ExcelJS.Cell} cell
 * @param {object} opts - { bg, bold, italic, color, align, wrap, size }
 */
const styleCell = (cell, opts = {}) => {
  const {
    bg = C.WHITE, bold = false, italic = false,
    color = "FF000000", align = "left", wrap = true, size = 10,
  } = opts;
  cell.fill = fill(bg);
  cell.font = { bold, italic, color: { argb: color }, size, name: "Calibri" };
  cell.alignment = { vertical: "middle", horizontal: align, wrapText: wrap };
  cell.border = border();
};

/** Write a section band header (dark teal, merged) */
const sectionBand = (ws, rowNum, fromCol, toCol, title) => {
  ws.mergeCells(rowNum, fromCol, rowNum, toCol);
  const cell = ws.getCell(rowNum, fromCol);
  cell.value = title;
  styleCell(cell, { bg: C.DARK_TEAL, bold: true, color: "FFE5F0F1", size: 11 });
  ws.getRow(rowNum).height = 22;
};

/** Write a sub-header (mid-teal, merged) */
const subHeader = (ws, rowNum, fromCol, toCol, title) => {
  ws.mergeCells(rowNum, fromCol, rowNum, toCol);
  const cell = ws.getCell(rowNum, fromCol);
  cell.value = title;
  styleCell(cell, { bg: C.MID_TEAL, bold: true, color: "FFE5F0F1", size: 10 });
  ws.getRow(rowNum).height = 18;
};

/** Write a column header cell */
const colHeader = (cell, text) => {
  cell.value = text;
  styleCell(cell, { bg: C.DARK_TEAL, bold: true, color: "FFE5F0F1", align: "center", size: 9, wrap: true });
};

/** Write a label cell (grey background) */
const labelCell = (cell, text) => {
  cell.value = text;
  styleCell(cell, { bg: C.LABEL_GREY });
};

/** Write a green auto-filled value cell */
const greenCell = (cell, value) => {
  cell.value = value ?? "";
  styleCell(cell, { bg: C.GREEN });
};

/** Write a yellow manual-entry cell */
const yellowCell = (cell, value = "") => {
  cell.value = value;
  styleCell(cell, { bg: C.YELLOW });
};

/** Write a light-yellow optional cell */
const ltYellowCell = (cell, value = "") => {
  cell.value = value;
  styleCell(cell, { bg: C.LT_YELLOW });
};

/** Write a total/sum row cell */
const totalCell = (cell, value) => {
  cell.value = value ?? "";
  styleCell(cell, { bg: C.TOTAL_BLUE, bold: true });
};

/** Write an ID cell (pale teal, centred, bold) */
const idCell = (cell, value) => {
  cell.value = value ?? "";
  styleCell(cell, { bg: C.PALE_TEAL, bold: true, align: "center" });
};

/** White / empty border cell */
const emptyCell = (cell) => {
  cell.value = "";
  styleCell(cell, { bg: C.WHITE });
};

const exportInfoRow = (ws, rowNum, toCol, formattedDateTime) => {
  ws.mergeCells(rowNum, 1, rowNum, toCol);
  const cell = ws.getCell(rowNum, 1);
  cell.value = `Exported on: ${formattedDateTime}`;
  styleCell(cell, {
    bg: C.NOTE_YELLOW,
    bold: true,
    italic: true,
    align: "left",
    size: 10,
  });
  ws.getRow(rowNum).height = 20;
};
/** Format a number for display in cells (up to 6 decimals, blank for 0) */
const fmtN = (v, d = 6) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? parseFloat(n.toFixed(d)) : "";
};

/** Format a date value */
const fmtDate = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleDateString("en-GB"); }
  catch { return String(v); }
};

/** Safe string fallback */
const dash = (v) => (v == null || v === "" ? "" : v);


// ─── Data builder helpers ─────────────────────────────────────────────────────

/**
 * Compute per-process data from FormD + FormA + FormF
 * (mirrors Summary1Installation processData memo)
 */
  export const buildProcessData = (allFormData) => {
  const fA = allFormData?.formA || {};
  const fD = allFormData?.formD || {};
  const fE = allFormData?.formE || {};
  const fF = allFormData?.formF || {};
  const dProcesses    = fD?.processes || [];
  const formAProcesses = fA.relevantProcesses || [];
  const purchasedPrecursors = fA.purchasedPrecursors || [];
  const formEPrecursors = fE?.precursors || [];
  // CN code now lives on the Purchased Precursors table (Form A tab 5),
  // not on Aggregated Goods (tab 4). Look it up via the precursor linked
  // to this process (linkedProcessId), not by goods category.
  const aggGoodsLookup = Object.fromEntries(
    (fA.aggregatedGoods || []).map(g => [g.category, g])
  );

  return dProcesses.map((proc, idx) => {
    const aProc = formAProcesses[idx] || {};
    const enriched = {
      ...proc,
      id:   proc.id   || proc.processId || aProc.id   || `P${idx + 1}`,
      name: proc.name || proc.title     || aProc.name || `P${idx + 1}`,
      aggregatedGoodsCategory:
        aProc.aggregatedGoodsCategory || proc.aggregatedGoodsCategory || "",
    };
    const see      = DFormulas.calculateSEE(enriched);
    const totalProd = see.productionLevelsTotalAuto || 0;
    const dirEm     = Number(proc.directlyAttributableEmissions) || 0;

    const heatNet =
      (Number(proc.measurableHeat?.amountImported) || 0) *
      (Number(proc.measurableHeat?.emissionFactorImported) || 0)
      - (Number(proc.measurableHeat?.amountExported) || 0) *
      (Number(proc.measurableHeat?.emissionFactorExported) || 0);

    const dirEmNet   = see.dirEm_net || 0;
    const wasteGasNet = dirEmNet - dirEm - heatNet;
    const indirEm    = see.indir_em || 0;
    const elecMWh    = Number(proc.electricityConsumption?.consumption || 0);
    const elecPerT   = totalProd > 0 ? elecMWh / totalProd : 0;

    const cpRow = ((fF?.carbonRows || fF?.carbonPriceRows) || [])
      .find(r => r.processId === enriched.id) || {};

    // CN code now sourced from the purchased precursor linked to this
    // process, not from aggregated goods (column removed from Form A tab 4).
    const linkedA = purchasedPrecursors.find(
      (pp) => pp.linkedProcessId === enriched.id
    );
    const linkedE = formEPrecursors.find(
      (ep) => ep.linkedProcessId === enriched.id
    );
    const cnCodeCompact =
      linkedA?.cnCode || linkedE?.emissions?.cnCode || "";
    const cnCode = getDisplayCodeForCompact(cnCodeCompact);

    return {
      id:           enriched.id,
      name:         enriched.name,
      goodsCat:     enriched.aggregatedGoodsCategory,
      cnCode,
      cnName:       getDescriptionForCompactCode(cnCodeCompact),
      dirEm,
      heatEm:       heatNet,
      wgEm:         wasteGasNet,
      totalDirect:  dirEmNet,
      indirect:     indirEm,
      activityLevel: totalProd,
      unit:         "t",
      see_direct:   see.see_direct,
      see_indirect: see.see_indirect,
      see_total:    see.see_total,
      elecMWh,
      elecPerT,
      elecSrc:      proc.electricityConsumption?.source || "",
      cpDue:        Number(cpRow.cp_due_per_produced_t_or_MWh || 0),
      rebate:       Number(cpRow.rebate_per_produced_t_or_MWh || 0),
      shareEmCovered: Number(cpRow.share_of_emissions_covered || 0),
      coveredSe:    Number(cpRow.covered_se_tCO2e || 0),
      effectiveCp:  Number(cpRow.result_effective_cp || 0),
      cpInstrument: fA.carbonPriceInstrument || "",
      currency:     fF?.currency || "EUR",
      // raw proc for detailed section
      _proc: enriched,
      _fF:   fF,
    };
  });
};

/**
 * Compute per-precursor data from FormA + FormE + FormF
 */
  export const buildPrecursorData = (allFormData) => { 
  const fA = allFormData?.formA || {};
  const fE = allFormData?.formE || {};
  const fF = allFormData?.formF || {};
  const purchPrecs  = fA.purchasedPrecursors || [];
  const ePrecursors = fE?.precursors || [];

  return purchPrecs.map((pp, idx) => {
    const ep  = ePrecursors.find(e => e.id === pp.id) || ePrecursors[idx] || {};
    const em  = ep.emissions || {};
    const seeDirect   = Number(em.seeDirect   || 0);
    const seeIndirect = Number(em.seeIndirect || 0);
    const cpRow = ((fF?.carbonRows || fF?.carbonPriceRows) || [])
      .find(r => r.processId === pp.id) || {};

    // CN code is now entered directly on the precursor row in Form A tab 5.
    const cnCodeCompact = pp.cnCode || "";
    const cnCode = getDisplayCodeForCompact(cnCodeCompact);

    return {
      id:           pp.id || `PP${idx + 1}`,
      name:         pp.name || "",
      // goodsType should reflect the goods category from Form A (not the
      // precursor `name`). Use `goodsCategory` from Form A and keep
      // `goodsType` for backward compatibility in export code.
      goodsCategory: pp.goodsCategory || pp.name || "",
      goodsType:    pp.goodsCategory || pp.name || "",
      cnCode,
      cnName:       getDescriptionForCompactCode(cnCodeCompact),
      seeDirect,
      seeIndirect,
      seeTotal:     seeDirect + seeIndirect,
      elecPerT:     Number(em.secIndirect || 0),
      elecSrc:      em.eefIndirectSource || "",
      isDefault:    em.sourceSeeDirect === "Default",
      countryCode:  pp.countryCode || "",
      cpInstrument: fA.carbonPriceInstrument || "",
      currency:     fF?.currency || "EUR",
      cpDue:        Number(cpRow.cp_due_per_produced_t_or_MWh || 0),
      rebate:       Number(cpRow.rebate_per_produced_t_or_MWh || 0),
      effectiveCp:  Number(cpRow.result_effective_cp || 0),
    };
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHEET 1 — Summary_Processes
// ═══════════════════════════════════════════════════════════════════════════════
const buildSummaryProcessesSheet = (ws, allFormData, formattedDateTime) => {
  const fA = allFormData?.formA || {};
  const fC = allFormData?.formC || {};
  const processData  = buildProcessData(allFormData);
  const precData     = buildPrecursorData(allFormData);
  const aggGoods     = fA.aggregatedGoods    || [];
  const processes    = fA.relevantProcesses  || [];
  const precursors   = fA.purchasedPrecursors || [];

  const totalDirect   = Number(fC?.directEmissions || fC?.totalDirectEmissions || 0);
  const totalIndirect = Number(fC?.indirectEmissions || fC?.totalIndirectEmissions || 0);
  const ghgTotals = {
    dirEm:       processData.reduce((s, p) => s + p.dirEm, 0),
    heatEm:      processData.reduce((s, p) => s + p.heatEm, 0),
    wgEm:        processData.reduce((s, p) => s + p.wgEm, 0),
    totalDirect: processData.reduce((s, p) => s + p.totalDirect, 0),
    indirect:    processData.reduce((s, p) => s + p.indirect, 0),
  };
  const restDirect    = totalDirect   - ghgTotals.totalDirect;
  const restIndirect  = totalIndirect - ghgTotals.indirect;
  const restDirPct    = totalDirect   ? (restDirect   / totalDirect   * 100).toFixed(2) + "%" : "—";
  const restIndirPct  = totalIndirect ? (restIndirect / totalIndirect * 100).toFixed(2) + "%" : "—";

  // ── Column setup (15 columns max width) ────────────────────────────────────
  const COL_W = [5,20,24,10,14,14,14,14,14,12,14,10,14,14,14];
  ws.columns = COL_W.map(w => ({ width: w }));

  let r = 1;
  exportInfoRow(ws, r, 15, formattedDateTime);
  r++;

  // ── SECTION 1 ───────────────────────────────────────────────────────────────
  sectionBand(ws, r, 1, 15, "1. Summary of the manufacturing facility, processes and production routes");
  r++;

  // 1.1 Manufacturing Facility
  subHeader(ws, r, 1, 15, "1. Summary of the manufacturing facility");
  r++;

  const instFields = [
    ["Name of the manufacturing facility (English name):", fA.installationNameEnglish || fA.installationName || ""],
    ["Street, Number:", fA.street || ""],
    ["Economic activity:", fA.economicActivity || ""],
    ["City:", fA.city || ""],
    ["Country:", fA.country || ""],
    ["UNLOCODE:", fA.unLocode || ""],
    ["Coordinates (latitude):", fA.latitude || ""],
    ["Coordinates (longitude):", fA.longitude || ""],
    ["Reporting period start:", fmtDate(fA.reportingPeriodStart)],
    ["Reporting period end:",   fmtDate(fA.reportingPeriodEnd)],
  ];

  instFields.forEach(([label, value]) => {
    const row = ws.getRow(r);
    labelCell(row.getCell(1), label);
    ws.mergeCells(r, 1, r, 2);
    greenCell(row.getCell(3), value);
    ws.mergeCells(r, 3, r, 6);
    // Carbon price / additional info on same rows
    if (label === "City:") {
      labelCell(row.getCell(8), "Carbon price instrument:");
      ws.mergeCells(r, 8, r, 10);
      yellowCell(row.getCell(11), fA.carbonPriceInstrument || "");
      ws.mergeCells(r, 11, r, 15);
    }
    if (label === "Country:") {
      labelCell(row.getCell(8), "Any additional information:");
      ws.mergeCells(r, 8, r, 10);
      yellowCell(row.getCell(11), fA.additionalInfo || "");
      ws.mergeCells(r, 11, r, 15);
    }
    r++;
  });

  r++;

  // 1.2 Aggregated goods table
  subHeader(ws, r, 1, 15,
    "2. Summary of the production processes, included precursors and production routes, where relevant");
  r++;

  // (a) Aggregated goods
  ["(a)", "Aggregated good produced", "Routes",
   "Route 1","Route 2","Route 3","Route 4","Route 5","Route 6"].forEach((h, i) => {
    colHeader(ws.getRow(r).getCell(i + 1), h);
  });
  r++;

  const activeGoods = aggGoods.filter(g => g.category);
  if (activeGoods.length === 0) {
    ws.mergeCells(r, 1, r, 9);
    const cell = ws.getCell(r, 1);
    cell.value = "No aggregated goods defined in Form A";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
    r++;
  } else {
    activeGoods.forEach(g => {
      const row = ws.getRow(r);
      idCell(row.getCell(1), g.id);
      greenCell(row.getCell(2), g.category);
      greenCell(row.getCell(3), g.primaryRoute || "All production routes");
      greenCell(row.getCell(4), g.route1 || "");
      greenCell(row.getCell(5), g.route2 || "");
      greenCell(row.getCell(6), g.route3 || "");
      greenCell(row.getCell(7), g.route4 || "");
      greenCell(row.getCell(8), g.route5 || "");
      greenCell(row.getCell(9), g.route6 || "");
      r++;
    });
  }

  r++;

  // (b) Production processes
  ["(b)", "Production process", "Aggregated goods category",
   "1","2","3","4","5","6"].forEach((h, i) => {
    colHeader(ws.getRow(r).getCell(i + 1), h);
  });
  r++;

  const activeProcs = processes.filter(p => p.name || p.aggregatedGoodsCategory);
  if (activeProcs.length === 0) {
    ws.mergeCells(r, 1, r, 9);
    const cell = ws.getCell(r, 1);
    cell.value = "No production processes defined in Form A";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
    r++;
  } else {
    activeProcs.forEach(p => {
      const row = ws.getRow(r);
      idCell(row.getCell(1), p.id);
      greenCell(row.getCell(2), p.name || "");
      greenCell(row.getCell(3), p.aggregatedGoodsCategory || "");
      greenCell(row.getCell(4), p.inc1 || "");
      greenCell(row.getCell(5), p.inc2 || "");
      greenCell(row.getCell(6), p.inc3 || "");
      greenCell(row.getCell(7), p.inc4 || "");
      greenCell(row.getCell(8), p.inc5 || "");
      greenCell(row.getCell(9), p.inc6 || "");
      r++;
    });
  }

  r++;

  // (c) Purchased precursors
  ["(c)", "Purchased precursor", "Aggregated goods category",
   "Route 1","Route 2","Route 3","Route 4","Route 5","Country code"].forEach((h, i) => {
    colHeader(ws.getRow(r).getCell(i + 1), h);
  });
  r++;

  const activePrecs = precursors.filter(p => p.name);
  if (activePrecs.length === 0) {
    ws.mergeCells(r, 1, r, 9);
    const cell = ws.getCell(r, 1);
    cell.value = "No purchased precursors defined in Form A";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
    r++;
  } else {
    activePrecs.forEach(pp => {
      const row = ws.getRow(r);
      idCell(row.getCell(1), pp.id);
      greenCell(row.getCell(2), pp.name);
      greenCell(row.getCell(3), pp.linkedProcessId || "");
      greenCell(row.getCell(4), pp.route1 || "");
      greenCell(row.getCell(5), pp.route2 || "");
      greenCell(row.getCell(6), pp.route3 || "");
      greenCell(row.getCell(7), pp.route4 || "");
      greenCell(row.getCell(8), pp.route5 || "");
      greenCell(row.getCell(9), pp.countryCode || "");
      r++;
    });
  }

  r += 2;

  // ── SECTION 2 ───────────────────────────────────────────────────────────────
  sectionBand(ws, r, 1, 15,
    "2. Greenhouse gas emissions balance and specific embedded emissions (SEE)");
  r++;

  // 2a — GHG balance
  subHeader(ws, r, 1, 15,
    "1. GHG emissions balance of the manufacturing facility and all production processes");
  r++;

  // (a) Per-process GHG table headers
  const ghgHeaders = [
    "(a)", "Production process", "Aggregated goods category",
    "Unit", "DirEm* (tCO2e)", "Heat emissions (tCO2e)",
    "Waste gas emissions (tCO2e)", "Total direct emissions (tCO2e)",
    "Indirect emissions (tCO2e)", "", "Activity level", "Unit",
  ];
  ghgHeaders.forEach((h, i) => colHeader(ws.getRow(r).getCell(i + 1), h));
  r++;

  if (processData.length === 0) {
    ws.mergeCells(r, 1, r, 12);
    const cell = ws.getCell(r, 1);
    cell.value = "No process data from Form D";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
    r++;
  } else {
    processData.forEach(p => {
      const row = ws.getRow(r);
      idCell(row.getCell(1), p.id);
      greenCell(row.getCell(2), p.name);
      greenCell(row.getCell(3), p.goodsCat);
      styleCell(row.getCell(4), { align: "center" }); row.getCell(4).value = "tCO2e";
      greenCell(row.getCell(5), fmtN(p.dirEm));
      greenCell(row.getCell(6), fmtN(p.heatEm));
      greenCell(row.getCell(7), fmtN(p.wgEm));
      greenCell(row.getCell(8), fmtN(p.totalDirect));
      greenCell(row.getCell(9), fmtN(p.indirect));
      emptyCell(row.getCell(10));
      greenCell(row.getCell(11), fmtN(p.activityLevel, 0) || "");
      styleCell(row.getCell(12), { align: "center" }); row.getCell(12).value = p.unit;
      r++;
    });

    // Totals row
    const tRow = ws.getRow(r);
    ws.mergeCells(r, 1, r, 3);
    totalCell(tRow.getCell(1), "Total");
    totalCell(tRow.getCell(4), "tCO2e");
    totalCell(tRow.getCell(5), fmtN(ghgTotals.dirEm));
    totalCell(tRow.getCell(6), fmtN(ghgTotals.heatEm));
    totalCell(tRow.getCell(7), fmtN(ghgTotals.wgEm));
    totalCell(tRow.getCell(8), fmtN(ghgTotals.totalDirect));
    totalCell(tRow.getCell(9), fmtN(ghgTotals.indirect));
    [10, 11, 12].forEach(c => totalCell(tRow.getCell(c), ""));
    r++;
  }

  r++;

  // (b) Comparison table
  const compHeaders = [
    "(b)", "Comparison", "Unit",
    "Total direct emissions (tCO2e)", "Total indirect emissions (tCO2e)",
  ];
  compHeaders.forEach((h, i) => colHeader(ws.getRow(r).getCell(i + 1), h));
  r++;

  [
    ["i.",   "Total emissions of the manufacturing facility (from Form C)", "tCO2e",
     fmtN(totalDirect), fmtN(totalIndirect)],
    ["ii.",  "Rest (= emissions not attributed to CBAM processes)", "tCO2e",
     fmtN(restDirect), fmtN(restIndirect)],
    ["iii.", "Rest (expressed as % of total)", "—",
     restDirPct, restIndirPct],
  ].forEach(([idx, desc, unit, dir, indir]) => {
    const row = ws.getRow(r);
    styleCell(row.getCell(1), { align: "center" }); row.getCell(1).value = idx;
    labelCell(row.getCell(2), desc);
    styleCell(row.getCell(3), { align: "center" }); row.getCell(3).value = unit;
    greenCell(row.getCell(4), dir);
    greenCell(row.getCell(5), indir);
    r++;
  });

  // Note row
  ws.mergeCells(r, 1, r, 5);
  const noteCell = ws.getCell(r, 1);
  noteCell.value =
    "Note: system boundaries may differ. Values for 'Rest' ≠ 0 are not necessarily indicative of omissions or errors.";
  styleCell(noteCell, { bg: C.NOTE_YELLOW, italic: true, size: 9, wrap: true });
  ws.getRow(r).height = 28;
  r += 2;

  // 2b — SEE table
  subHeader(ws, r, 1, 15, "2. Specific direct and indirect (embedded) emissions");
  r++;

  // (a) Processes SEE
  const seeHdrRow = ws.getRow(r);
  ["(a)", "Production process", "Aggregated goods category",
   "SE (direct)\ntCO2e/t", "SEE (direct)\ntCO2e/t",
   "SE (indirect)\ntCO2e/t", "SEE (indirect)\ntCO2e/t",
   "Elec. consumed\nMWh/t", "Embedded electricity\nMWh/t",
   "CP due\n(per t or MWh)", "Rebate\n(per t or MWh)",
  ].forEach((h, i) => colHeader(seeHdrRow.getCell(i + 1), h));
  ws.getRow(r).height = 32;
  r++;

  processData.forEach(p => {
    const row = ws.getRow(r);
    idCell(row.getCell(1), p.id);
    greenCell(row.getCell(2), p.name);
    greenCell(row.getCell(3), p.goodsCat);
    // SE (direct) = same as SEE here until precursor breakdown is available
    greenCell(row.getCell(4), fmtN(p.see_direct));
    greenCell(row.getCell(5), fmtN(p.see_direct));
    greenCell(row.getCell(6), fmtN(p.see_indirect));
    greenCell(row.getCell(7), fmtN(p.see_indirect));
    greenCell(row.getCell(8), fmtN(p.elecPerT));
    greenCell(row.getCell(9), fmtN(p.elecPerT));
    greenCell(row.getCell(10), fmtN(p.cpDue));
    greenCell(row.getCell(11), fmtN(p.rebate));
    r++;
  });

  if (processData.length === 0) {
    ws.mergeCells(r, 1, r, 11);
    const cell = ws.getCell(r, 1);
    cell.value = "No process data from Form D";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
    r++;
  }

  r++;

  // (b) Precursors SEE
  const precSeeHdrRow = ws.getRow(r);
  ["(b)", "Purchased precursor", "Type of precursor",
   "", "SEE (direct)\ntCO2e/t", "",
   "SEE (indirect)\ntCO2e/t", "Elec. consumed\nMWh/t",
  ].forEach((h, i) => colHeader(precSeeHdrRow.getCell(i + 1), h));
  ws.getRow(r).height = 32;
  r++;

  if (precData.length === 0) {
    ws.mergeCells(r, 1, r, 8);
    const cell = ws.getCell(r, 1);
    cell.value = "No purchased precursors defined";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
    r++;
  } else {
    precData.forEach(pp => {
      const row = ws.getRow(r);
      idCell(row.getCell(1), pp.id);
      greenCell(row.getCell(2), pp.name);
      greenCell(row.getCell(3), pp.goodsType);
      emptyCell(row.getCell(4));
      greenCell(row.getCell(5), fmtN(pp.seeDirect));
      emptyCell(row.getCell(6));
      greenCell(row.getCell(7), fmtN(pp.seeIndirect));
      greenCell(row.getCell(8), fmtN(pp.elecPerT));
      r++;
    });
  }

  r += 2;

  // ── SECTION 3 — Detailed overview ──────────────────────────────────────────
  sectionBand(ws, r, 1, 15, "3. Detailed overview of each production process");
  r++;

  const detailHeaders = [
    "Row", "Aggregated goods category", "Mass share\n(t/t)",
    "(Share of)\nDefault value", "SEE (direct)\ntCO2e/t",
    "SEE (indirect)\ntCO2e/t", "SEE (total)\ntCO2e/t",
    "EmbEm (direct)\ntCO2e", "EmbEm (indirect)\ntCO2e",
    "EmbEm (total)\ntCO2e", "Source of\nElectricity EF",
    "Embedded electricity\nMWh/t", "Country\ncode",
    "CP due\n(per t or MWh)", "Rebate\n(per t or MWh)",
  ];

  if (processData.length === 0) {
    ws.mergeCells(r, 1, r, 15);
    const cell = ws.getCell(r, 1);
    cell.value = "No process data from Form D";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
    r++;
  } else {
    processData.forEach(proc => {
      // Process sub-header
      ws.mergeCells(r, 1, r, 15);
      const procHdrCell = ws.getCell(r, 1);
      procHdrCell.value = `${proc.id} — ${proc.name}  |  ${proc.goodsCat}`;
      styleCell(procHdrCell, { bg: C.PALE_TEAL, bold: true, size: 10 });
      r++;

      // Column headers
      detailHeaders.forEach((h, i) => colHeader(ws.getRow(r).getCell(i + 1), h));
      ws.getRow(r).height = 36;
      r++;

      const embDirect   = proc.see_direct   * proc.activityLevel;
      const embIndirect = proc.see_indirect * proc.activityLevel;
      const embTotal    = embDirect + embIndirect;

      // Total row
      const totalRow = ws.getRow(r);
      idCell(totalRow.getCell(1), "Total");
      greenCell(totalRow.getCell(2), proc.goodsCat);
      emptyCell(totalRow.getCell(3));
      emptyCell(totalRow.getCell(4));
      greenCell(totalRow.getCell(5), fmtN(proc.see_direct));
      greenCell(totalRow.getCell(6), fmtN(proc.see_indirect));
      greenCell(totalRow.getCell(7), fmtN(proc.see_direct + proc.see_indirect));
      greenCell(totalRow.getCell(8), fmtN(embDirect));
      greenCell(totalRow.getCell(9), fmtN(embIndirect));
      greenCell(totalRow.getCell(10), fmtN(embTotal));
      emptyCell(totalRow.getCell(11));
      greenCell(totalRow.getCell(12), fmtN(proc.elecPerT));
      emptyCell(totalRow.getCell(13));
      greenCell(totalRow.getCell(14), fmtN(proc.cpDue));
      greenCell(totalRow.getCell(15), fmtN(proc.rebate));
      r++;

      // Consumed precursor sub-rows
      const consumedPrecs = (
        proc._proc?.consumedOtherProcesses ||
        proc._proc?.consumedInProcesses ||
        []
      );
      const ePrecursors = allFormData?.formE?.precursors || [];
      const fAPrecursors = allFormData?.formA?.purchasedPrecursors || [];

      if (consumedPrecs.length === 0) {
        ws.mergeCells(r, 1, r, 15);
        const cell = ws.getCell(r, 1);
        cell.value = "No purchased precursors consumed in this process";
        styleCell(cell, { bg: C.LABEL_GREY, italic: true, size: 9, align: "center" });
        r++;
      } else {
        consumedPrecs.forEach(cp2 => {
          const pp  = fAPrecursors.find(p => p.id === cp2.precursorId) || {};
          const ep  = ePrecursors.find(e => e.id === cp2.precursorId)  || {};
          const qty = Number(cp2.amounts || 0);
          const massShare    = proc.activityLevel > 0 ? qty / proc.activityLevel : 0;
          const cpSeeDirect  = Number(ep.emissions?.seeDirect   || 0) * massShare;
          const cpSeeIndir   = Number(ep.emissions?.seeIndirect || 0) * massShare;
          const cpEmbDir     = cpSeeDirect  * proc.activityLevel;
          const cpEmbIndir   = cpSeeIndir   * proc.activityLevel;
          const isDefault    = ep.emissions?.sourceSeeDirect === "Default";

          const row = ws.getRow(r);
          idCell(row.getCell(1), pp.id || cp2.precursorId || "");
          greenCell(row.getCell(2), pp.name || "");
          greenCell(row.getCell(3), fmtN(massShare));
          greenCell(row.getCell(4), isDefault ? "TRUE" : "FALSE");
          greenCell(row.getCell(5), fmtN(cpSeeDirect));
          greenCell(row.getCell(6), fmtN(cpSeeIndir));
          greenCell(row.getCell(7), fmtN(cpSeeDirect + cpSeeIndir));
          greenCell(row.getCell(8), fmtN(cpEmbDir));
          greenCell(row.getCell(9), fmtN(cpEmbIndir));
          greenCell(row.getCell(10), fmtN(cpEmbDir + cpEmbIndir));
          greenCell(row.getCell(11), ep.emissions?.eefIndirectSource || "");
          greenCell(row.getCell(12), fmtN(Number(ep.emissions?.secIndirect || 0) * massShare));
          greenCell(row.getCell(13), pp.countryCode || "");
          emptyCell(row.getCell(14));
          emptyCell(row.getCell(15));
          r++;
        });
      }
      r++;
    });
  }
};


// ═══════════════════════════════════════════════════════════════════════════════
// SHEET 2 — Summary_Products (all 44 columns)
// ═══════════════════════════════════════════════════════════════════════════════
export const PRODUCT_COLS = [
  { n:  1, label: "Production process",                                   w: 22, type: "id"        },
  { n:  2, label: "Type of aggregated good or precursor",                 w: 24, type: "green"     },
  { n:  3, label: "CN Codes",                                             w: 12, type: "green"     },
  { n:  4, label: "CN Name",                                              w: 22, type: "green"     },
  { n:  5, label: "Product name (for invoices)",                          w: 24, type: "yellow"    },
  { n:  6, label: "SEE (direct)\ntCO2e/t",                               w: 14, type: "green"     },
  { n:  7, label: "SEE (indirect)\ntCO2e/t",                             w: 14, type: "green"     },
  { n:  8, label: "SEE (total)\ntCO2e/t",                                w: 14, type: "green"     },
  { n:  9, label: "Unit",                                                 w:  9, type: "green"     },
  { n: 10, label: "Share of emissions by default value",                  w: 16, type: "green"     },
  { n: 11, label: "Source for electricity EF",                            w: 18, type: "green"     },
  { n: 12, label: "Embedded electricity\n(MWh/t)",                       w: 14, type: "green"     },
  { n: 13, label: "Main reducing agent",                                  w: 18, type: "yellow"    },
  { n: 14, label: "Steel mill ID number",                                 w: 16, type: "yellow"    },
  { n: 15, label: "% Mn",                                                 w:  9, type: "lt_yellow" },
  { n: 16, label: "% Cr",                                                 w:  9, type: "lt_yellow" },
  { n: 17, label: "% Ni",                                                 w:  9, type: "lt_yellow" },
  { n: 18, label: "% other alloys",                                       w: 12, type: "lt_yellow" },
  { n: 19, label: "% carbon",                                             w: 10, type: "lt_yellow" },
  { n: 20, label: "t scrap per t steel",                                  w: 14, type: "lt_yellow" },
  { n: 21, label: "% other materials",                                    w: 14, type: "lt_yellow" },
  { n: 22, label: "% pre-consumer scrap",                                 w: 14, type: "lt_yellow" },
  { n: 23, label: "t scrap per t aluminium",                              w: 16, type: "lt_yellow" },
  { n: 24, label: "% non-aluminium elements",                             w: 16, type: "lt_yellow" },
  { n: 25, label: "Clinker factor",                                       w: 12, type: "yellow"    },
  { n: 26, label: "Calcined or not",                                      w: 12, type: "yellow"    },
  { n: 27, label: "Concentration (if hydrous solution)",                  w: 18, type: "yellow"    },
  { n: 28, label: "% nitric acid",                                        w: 12, type: "yellow"    },
  { n: 29, label: "% urea",                                               w: 10, type: "yellow"    },
  { n: 30, label: "% N contained",                                        w: 12, type: "yellow"    },
  { n: 31, label: "% N as ammonium (NH4+)",                              w: 16, type: "yellow"    },
  { n: 32, label: "% N as nitrate (NO3-)",                               w: 16, type: "yellow"    },
  { n: 33, label: "% N as Urea",                                          w: 12, type: "yellow"    },
  { n: 34, label: "% N in other (organic) forms",                         w: 18, type: "yellow"    },
  { n: 35, label: "Type of instrument (carbon pricing)",                  w: 20, type: "lt_yellow" },
  { n: 36, label: "Share of total embedded emissions covered by CP",      w: 20, type: "lt_yellow" },
  { n: 37, label: "Embedded emissions covered by the carbon price",       w: 20, type: "green"     },
  { n: 38, label: "Currency",                                             w: 10, type: "lt_yellow" },
  { n: 39, label: "CP due (per produced t or MWh)",                      w: 18, type: "lt_yellow" },
  { n: 40, label: "Type of rebate",                                       w: 14, type: "lt_yellow" },
  { n: 41, label: "Share of embedded emissions covered by rebate",        w: 20, type: "lt_yellow" },
  { n: 42, label: "Embedded emissions covered by rebate",                 w: 20, type: "green"     },
  { n: 43, label: "Amount of rebate (per produced t or MWh)",             w: 18, type: "lt_yellow" },
  { n: 44, label: "Result: Effective CP due (per produced t or MWh)",     w: 22, type: "green"     },
];
const MANUAL_COLS = new Set([5, 13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,40,41]);
const buildSummaryProductsSheet = (ws, allFormData, formattedDateTime, manualData = {}) => {
  const fA = allFormData?.formA || {};
  const processRows  = buildProcessData(allFormData);
  const precRows     = buildPrecursorData(allFormData);

  ws.columns = PRODUCT_COLS.map(c => ({ width: c.w }));

  let r = 1;
  exportInfoRow(ws, r, 44, formattedDateTime);
r++;


  // Section header
  sectionBand(ws, r, 1, 44, "2. Summary of products — all 44 columns (Summary_Products)");
  r++;

  // Info row
  ws.mergeCells(r, 1, r, 44);
  const infoCell = ws.getCell(r, 1);
  infoCell.value =
    "🟩 Green = auto-filled from Forms A–F   🟨 Yellow = required manual entry   🟡 Light yellow = optional / conditional";
  styleCell(infoCell, { bg: C.NOTE_YELLOW, italic: true, size: 9 });
  r++;

  // Group header row
  const groups = [
    { label: "Product Identification",              from: 1,  to: 5  },
    { label: "Specific Embedded Emissions (SEE) & Electricity — Form D/E", from: 6,  to: 12 },
    { label: "Additional Product Parameters (manual entry)",               from: 13, to: 34 },
    { label: "Carbon Pricing — Form A/F",           from: 35, to: 44 },
  ];
  const groupColors = ["FF254244", "FF1A5C41", "FF5A3800", "FF2A3C7A"];
  groups.forEach((g, i) => {
    ws.mergeCells(r, g.from, r, g.to);
    const cell = ws.getCell(r, g.from);
    cell.value = g.label;
    styleCell(cell, { bg: groupColors[i], bold: true, color: "FFFFFFFF", align: "center", size: 10 });
  });
  ws.getRow(r).height = 18;
  r++;

  // Column header row
  PRODUCT_COLS.forEach((col, i) => {
    const cell = ws.getRow(r).getCell(i + 1);
    cell.value = `[${col.n}]\n${col.label}`;
    styleCell(cell, { bg: C.DARK_TEAL, bold: true, color: "FFE5F0F1",
      align: "center", size: 8.5, wrap: true });
  });
  ws.getRow(r).height = 52;
  r++;

  /**
   * Write a single data row from a process or precursor data object.
   * Manual cells are left yellow/lt_yellow with empty values for user entry.
   */
  const writeDataRow = (rowData, isProc) => {
    const dataRow = ws.getRow(r);
    const rowKey = isProc ? `proc-${rowData.id}` : null;
    const manual = rowKey ? (manualData[rowKey] || {}) : {};

    PRODUCT_COLS.forEach(col => {
      const cell = dataRow.getCell(col.n);

      // Determine value for auto-filled columns
      let val = "";
      if (col.type !== "yellow" && col.type !== "lt_yellow") {
        switch (col.n) {
          case  1: val = rowData.id;                    break;
          case  2: val = rowData.goodsCat || rowData.name || ""; break;
          case  3: val = rowData.cnCode || "";          break;
          case  4: val = rowData.cnName || "";          break;
          case  6: val = fmtN(rowData.seeDirect   ?? rowData.see_direct);  break;
          case  7: val = fmtN(rowData.seeIndirect ?? rowData.see_indirect); break;
          case  8: val = fmtN(rowData.seeTotal    ?? (rowData.see_direct + rowData.see_indirect)); break;
          case  9: val = "t";                     break;
          case 10: val = isProc ? "" : (rowData.isDefault ? "TRUE" : "FALSE"); break;
          case 11: val = rowData.elecSrc || "";         break;
          case 12: val = fmtN(rowData.elecPerT);        break;
          case 37: val = fmtN(isProc
            ? rowData.shareEmCovered * (rowData.see_total || 0)
            : "");                                      break;
          case 42: val = fmtN(isProc
            ? rowData.rebate * (rowData.see_total || 0)
            : "");                                      break;
          case 44: val = fmtN(rowData.effectiveCp);     break;
          default: val = "";
        }
      }

     // lt_yellow auto-filled
      if (col.type === "lt_yellow") {
        switch (col.n) {
          case 35: val = rowData.cpInstrument || ""; break;
          case 36: val = fmtN(rowData.shareEmCovered) || ""; break;
          case 38: val = rowData.currency || "EUR"; break;
          case 39: val = fmtN(rowData.cpDue) || ""; break;
          case 43: val = fmtN(rowData.rebate) || ""; break;
          default: val = "";
        }
      }

     //  Manual entry override — product name, composition %, rebate type.
      //  CN code / CN name are NOT manual — they come only from Form A (set above).
      if (MANUAL_COLS.has(col.n)) {
        val = manual[col.n] || "";
      }

      // Apply styling
      if (col.n === 1) {
        idCell(cell, val);
      } else if (col.type === "green") {
        greenCell(cell, val);
      } else if (col.type === "yellow") {
        yellowCell(cell, val);
      } else if (col.type === "lt_yellow") {
        ltYellowCell(cell, val);
      }
    });

    ws.getRow(r).height = 18;
  };

  // Production process rows
  if (processRows.length > 0) {
    ws.mergeCells(r, 1, r, 44);
    const divCell = ws.getCell(r, 1);
    divCell.value = `Production processes ← Form D  (${processRows.length} row${processRows.length !== 1 ? "s" : ""})`;
    styleCell(divCell, { bg: C.MID_TEAL, bold: true, color: "FFE5F0F1", size: 9 });
    r++;

    processRows.forEach(row => { writeDataRow(row, true); r++; });
  }

  // Precursor rows
  if (precRows.length > 0) {
    ws.mergeCells(r, 1, r, 44);
    const divCell = ws.getCell(r, 1);
    divCell.value = `Purchased precursors ← Form A / E  (${precRows.length} row${precRows.length !== 1 ? "s" : ""})`;
    styleCell(divCell, { bg: C.MID_TEAL, bold: true, color: "FFE5F0F1", size: 9 });
    r++;

    precRows.forEach(row => { writeDataRow(row, false); r++; });
  }

  if (processRows.length === 0 && precRows.length === 0) {
    ws.mergeCells(r, 1, r, 44);
    const cell = ws.getCell(r, 1);
    cell.value = "No data yet — complete Forms A–F to populate this table.";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
  }
};


// ═══════════════════════════════════════════════════════════════════════════════
// SHEET 3 — Summary_Communication (44-column product table)
// ═══════════════════════════════════════════════════════════════════════════════
  const buildSummaryCommunicationSheet = (ws, allFormData, formattedDateTime, manualData = {}) => {
  const fA = allFormData?.formA || {};
  const fC = allFormData?.formC || {};
  const processRows = buildProcessData(allFormData);
  const precRows    = buildPrecursorData(allFormData);
  const aggGoods    = fA.aggregatedGoods    || [];
  const procs       = fA.relevantProcesses  || [];

  const calcBased   = Number(fC?.ghgBalanceByMethod?.calculationBased) || 0;
  const pfcTotal    = Number(fC?.ghgBalanceByMethod?.totalPFC)         || 0;
  const measBased   = Number(fC?.ghgBalanceByMethod?.measurementBased) || 0;
  const otherEm     = Number(fC?.ghgBalanceByMethod?.other)            || 0;
  const totalDirect = Number(fC?.directEmissions || fC?.totalDirectEmissions || 0);
  const totalIndir  = Number(fC?.indirectEmissions || fC?.totalIndirectEmissions || 0);
  const totalAll    = totalDirect + totalIndir;

  // Use 17 columns for Section 1 table (matches UI layout)
  ws.columns = Array(17).fill(null).map((_, i) => ({
    width: [38,24,6,28,16,16,16,6,28,20,28,14,24,14,22,24,24][i] || 18,
  }));

  let r = 1;
  exportInfoRow(ws, r, 44, formattedDateTime);
r++;

  // ── SECTION 1 ───────────────────────────────────────────────────────────────
  sectionBand(ws, r, 1, 17, "1. Summary of the manufacturing facility and production processes");
  r++;

  // Group headers for Section 1 table
  const sec1GroupHdrs = [
    { label: "1. Manufacturing facility details ← Form A", from: 1, to: 2 },
    { label: "2. Production processes & routes ← Form A", from: 3, to: 10 },
    { label: "3. Emissions by monitoring methodology ← Form C", from: 11, to: 14 },
    { label: "4. Further information ← Form A", from: 15, to: 17 },
  ];
  sec1GroupHdrs.forEach(g => {
    ws.mergeCells(r, g.from, r, g.to);
    const cell = ws.getCell(r, g.from);
    cell.value = g.label;
    styleCell(cell, { bg: C.MID_TEAL, bold: true, color: "FFE5F0F1", align: "center", size: 9 });
  });
  ws.getRow(r).height = 18;
  r++;

  // Column headers for Section 1
  [
    "Parameter", "Value",
    "#", "(a) Aggregated good produced", "Route 1", "Route 2", "Route 3",
    "#", "(b) Production process", "Included goods",
    "Methodology", "tCO2e", "Total", "tCO2e",
    "Data quality", "Parameter", "Value",
  ].forEach((h, i) => colHeader(ws.getRow(r).getCell(i + 1), h));
  ws.getRow(r).height = 28;
  r++;

  // Section 1 data rows (9 rows matching UI)
  const instRows = [
    {
      lbl: "Name of the manufacturing facility (English name):",
      val: fA.installationNameEnglish || fA.installationName || "",
      mLbl: "Calculation-based (excl. PFC):", mVal: fmtN(calcBased),
      tLbl: "Total direct emissions:", tVal: fmtN(totalDirect),
      dq: fC?.dataQuality?.generalInfo   || "",
      fLbl: "Carbon price instrument:", fVal: fA.carbonPriceInstrument || "",
    },
    {
      lbl: "Street, Number:", val: fA.street || "",
      mLbl: "Total PFC emissions:", mVal: fmtN(pfcTotal),
      tLbl: "Total indirect emissions:", tVal: fmtN(totalIndir),
      dq: fC?.dataQuality?.justification || "",
      fLbl: "Any additional information:", fVal: fA.additionalInfo || "",
    },
    {
      lbl: "Economic activity:", val: fA.economicActivity || "",
      mLbl: "Measurement-based:", mVal: fmtN(measBased),
      tLbl: "Total emissions:", tVal: fmtN(totalAll),
      dq: fC?.dataQuality?.assurance     || "", fLbl: null, fVal: null,
    },
    {
      lbl: "Country:", val: fA.country || "",
      mLbl: "Other:", mVal: fmtN(otherEm),
      tLbl: null, tVal: null, dq: null, fLbl: null, fVal: null,
    },
    {
      lbl: "UNLOCODE:", val: fA.unLocode || "",
      mLbl: null, mVal: null, tLbl: null, tVal: null, dq: null, fLbl: null, fVal: null,
    },
    {
      lbl: "Coordinates (latitude):", val: fA.latitude || "",
      mLbl: null, mVal: null, tLbl: null, tVal: null, dq: null, fLbl: null, fVal: null,
    },
    {
      lbl: "Coordinates (longitude):", val: fA.longitude || "",
      mLbl: null, mVal: null, tLbl: null, tVal: null, dq: null, fLbl: null, fVal: null,
    },
    {
      lbl: "Reporting period start:", val: fmtDate(fA.reportingPeriodStart),
      mLbl: null, mVal: null, tLbl: null, tVal: null, dq: null, fLbl: null, fVal: null,
    },
    {
      lbl: "Reporting period end:", val: fmtDate(fA.reportingPeriodEnd),
      mLbl: null, mVal: null, tLbl: null, tVal: null, dq: null, fLbl: null, fVal: null,
    },
  ];

  instRows.forEach((ir, rowIdx) => {
    const row  = ws.getRow(r);
    const g    = aggGoods[rowIdx] || {};
    const p    = procs[rowIdx]    || {};

    labelCell(row.getCell(1), ir.lbl);
    greenCell(row.getCell(2), ir.val);
    idCell(row.getCell(3), g.id || "");
    greenCell(row.getCell(4), g.category || "");
    greenCell(row.getCell(5), g.route1 || "");
    greenCell(row.getCell(6), g.route2 || "");
    greenCell(row.getCell(7), g.route3 || "");
    idCell(row.getCell(8), p.id || "");
    greenCell(row.getCell(9), p.name || "");
    greenCell(row.getCell(10), [p.inc1, p.inc2, p.inc3].filter(Boolean).join(", ") || "");

    if (ir.mLbl) { labelCell(row.getCell(11), ir.mLbl); greenCell(row.getCell(12), ir.mVal); }
    else { emptyCell(row.getCell(11)); emptyCell(row.getCell(12)); }

    if (ir.tLbl) {
      const isTot = ir.tLbl.includes("Total emissions:");
      labelCell(row.getCell(13), ir.tLbl);
      if (isTot) totalCell(row.getCell(14), ir.tVal);
      else       greenCell(row.getCell(14), ir.tVal);
    } else { emptyCell(row.getCell(13)); emptyCell(row.getCell(14)); }

    if (ir.dq != null) greenCell(row.getCell(15), ir.dq);
    else emptyCell(row.getCell(15));

    if (ir.fLbl) { labelCell(row.getCell(16), ir.fLbl); greenCell(row.getCell(17), ir.fVal); }
    else { emptyCell(row.getCell(16)); emptyCell(row.getCell(17)); }

    r++;
  });

  r += 2;

  // ── SECTION 2 — 44-column product communication table ──────────────────────
  sectionBand(ws, r, 1, 44, "2. Summary of products — Communication with reporting declarants (44 columns)");
  r++;

  // Group header row for Section 2 (reuse PRODUCT_COLS groups)
  const commGroups = [
    { label: "Product identification",                              from:  1, to:  5, bg: "FF254244" },
    { label: "SEE & Electricity ← Form D/E",                       from:  6, to: 12, bg: "FF1A5C41" },
    { label: "Additional product parameters (manual entry)",        from: 13, to: 34, bg: "FF5A3800" },
    { label: "Carbon pricing ← Form A/F",                          from: 35, to: 44, bg: "FF2A3C7A" },
  ];
  commGroups.forEach(g => {
    ws.mergeCells(r, g.from, r, g.to);
    const cell = ws.getCell(r, g.from);
    cell.value = g.label;
    styleCell(cell, { bg: g.bg, bold: true, color: "FFFFFFFF", align: "center", size: 10 });
  });
  ws.getRow(r).height = 18;
  r++;

  // Extend worksheet columns to 44 for Section 2
  // (We resize columns from 18 onward for the wider table)
  for (let i = 18; i <= 44; i++) {
    ws.getColumn(i).width = PRODUCT_COLS[i - 1]?.w || 14;
  }

  // Column header row (reuse same 44 labels as Summary_Products sheet)
  PRODUCT_COLS.forEach((col, i) => {
    const cell = ws.getRow(r).getCell(i + 1);
    cell.value = `[${col.n}]\n${col.label}`;
    styleCell(cell, { bg: C.DARK_TEAL, bold: true, color: "FFE5F0F1",
      align: "center", size: 8.5, wrap: true });
  });
  ws.getRow(r).height = 52;
  r++;

  // Write data rows (same logic as Summary_Products but for communication context)
 const writeCommRow = (rowData, isProc) => {
    const dataRow = ws.getRow(r);
    const rowKey = isProc ? `proc-${rowData.id}` : null;
    const manual = rowKey ? (manualData[rowKey] || {}) : {};

    PRODUCT_COLS.forEach(col => {
      const cell = dataRow.getCell(col.n);
      let val = "";

      if (col.type === "green" || col.type === "id") {
        switch (col.n) {
          case  1: val = rowData.id;                                       break;
          case  2: val = rowData.goodsCat || rowData.name || "";           break;
          case  3: val = rowData.cnCode || "";                             break;
          case  4: val = rowData.cnName || "";                             break;
          case  6: val = fmtN(rowData.see_direct   ?? rowData.seeDirect);  break;
          case  7: val = fmtN(rowData.see_indirect ?? rowData.seeIndirect);break;
          case  8: val = fmtN(rowData.see_total    ?? rowData.seeTotal);   break;
          case  9: val = "t";                                              break;
          case 10: val = isProc ? "" : (rowData.isDefault ? "TRUE" : "FALSE"); break;
          case 11: val = dash(rowData.elecSrc);                            break;
          case 12: val = fmtN(rowData.elecPerT);                           break;
          case 37: val = fmtN(isProc
            ? rowData.shareEmCovered * (rowData.see_total || 0) : "");    break;
          case 42: val = fmtN(isProc
            ? rowData.rebate * (rowData.see_total || 0) : "");            break;
          case 44: val = fmtN(rowData.effectiveCp);                        break;
          default: val = "";
        }
      }

      if (col.type === "lt_yellow") {
        switch (col.n) {
          case 35: val = dash(rowData.cpInstrument);                       break;
          case 36: val = fmtN(rowData.shareEmCovered) || "";              break;
          case 38: val = rowData.currency || "EUR";                        break;
          case 39: val = fmtN(rowData.cpDue) || "";                       break;
          case 43: val = fmtN(rowData.rebate) || "";                      break;
          default: val = "";
        }
      }

      //  Manual entry override — product name, composition %, rebate type.
      //  CN code / CN name are NOT manual — they come only from Form A (set above).
      if (MANUAL_COLS.has(col.n)) {
        val = manual[col.n] || "";
      }

      if (col.n === 1)                idCell(cell, val);
      else if (col.type === "green")  greenCell(cell, val);
      else if (col.type === "yellow") yellowCell(cell, val);
      else if (col.type === "lt_yellow") ltYellowCell(cell, val);
    });

    ws.getRow(r).height = 18;
  };

  // Production process rows
  if (processRows.length > 0) {
    ws.mergeCells(r, 1, r, 44);
    const divCell = ws.getCell(r, 1);
    divCell.value = `Production processes ← Form D  (${processRows.length} row${processRows.length !== 1 ? "s" : ""})`;
    styleCell(divCell, { bg: C.MID_TEAL, bold: true, color: "FFE5F0F1", size: 9 });
    r++;
    processRows.forEach(row => { writeCommRow(row, true); r++; });
  }

  // Precursor rows
  if (precRows.length > 0) {
    ws.mergeCells(r, 1, r, 44);
    const divCell = ws.getCell(r, 1);
    divCell.value = `Purchased precursors ← Form A / E  (${precRows.length} row${precRows.length !== 1 ? "s" : ""})`;
    styleCell(divCell, { bg: C.MID_TEAL, bold: true, color: "FFE5F0F1", size: 9 });
    r++;
    precRows.forEach(row => { writeCommRow(row, false); r++; });
  }

  if (processRows.length === 0 && precRows.length === 0) {
    ws.mergeCells(r, 1, r, 44);
    const cell = ws.getCell(r, 1);
    cell.value = "No data yet — complete Forms A–F to populate this table.";
    styleCell(cell, { bg: C.LABEL_GREY, italic: true, align: "center" });
  }
};


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTION
// ═══════════════════════════════════════════════════════════════════════════════
/**
 * exportSummaryExcel — generate and download the full CBAM Summary Excel.
 *
 * @param {object} allFormData - The full FormContext allFormData object
 *   Expected shape: { formA, formB, formC, formD, formE, formF }
 */
const exportSummaryExcel = async (allFormData, manualData = {}) => {
  try {
    const workbook = new ExcelJS.Workbook();
    workbook.creator   = "OV Auditor";
    workbook.created   = new Date();
    workbook.modified  = new Date();

    const formattedDateTime = new Date().toLocaleString("en-GB", {
  dateStyle: "short",
  timeStyle: "medium",
});

    const processSheet       = workbook.addWorksheet("Summary_Processes");
    const productsSheet      = workbook.addWorksheet("Summary_Products");
    const communicationSheet = workbook.addWorksheet("Summary_Communication");

    buildSummaryProcessesSheet(processSheet, allFormData, formattedDateTime);
buildSummaryProductsSheet(productsSheet, allFormData, formattedDateTime, manualData);
buildSummaryCommunicationSheet(communicationSheet, allFormData, formattedDateTime, manualData);

    // Freeze panes on top rows of each sheet
    processSheet.views       = [{ state: "frozen", ySplit: 1 }];
    productsSheet.views      = [{ state: "frozen", ySplit: 5 }]; // below group + col headers
    communicationSheet.views = [{ state: "frozen", ySplit: 1 }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "CBAM_Summary_Styled.xlsx"
    );
  } catch (error) {
    console.error("CBAM Summary export failed:", error);
    throw error;
  }
};

export default exportSummaryExcel;